import { useEffect, useId, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Maximize2, Minimize2, X } from 'lucide-react'
import { useFocusTrap } from '../hooks/useFocusTrap'

export interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  /** sm 20rem · md 28rem · lg 36rem · xl 48rem */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  maximizable?: boolean
  defaultMaximized?: boolean
  /** sticky footer, usually the buttons */
  footer?: ReactNode
}

const SIZES = { sm: 'sm:max-w-xs', md: 'sm:max-w-md', lg: 'sm:max-w-xl', xl: 'sm:max-w-3xl' }

/** Right slide-in panel for detail views and substantial forms (CRM kit rule 1). Esc and backdrop close; focus trapped. */
export function Drawer({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = 'md',
  maximizable = true,
  defaultMaximized = false,
  footer,
}: DrawerProps) {
  const [maximized, setMaximized] = useState(defaultMaximized)
  const titleId = useId()
  const panelRef = useFocusTrap<HTMLDivElement>(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) setMaximized(defaultMaximized)
  }, [isOpen, defaultMaximized])

  if (!isOpen) return null
  const width = maximized ? 'sm:max-w-full' : SIZES[size]

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`absolute right-0 top-0 flex h-full w-full ${width} flex-col border-l border-slate-200 bg-white shadow-2xl outline-none animate-slide-in dark:border-slate-700 dark:bg-surface-dark`}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="truncate text-sm font-bold text-default">
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 truncate text-2xs text-muted">{subtitle}</p>}
          </div>
          <div className="ml-3 flex shrink-0 items-center gap-1">
            {maximizable && (
              <button
                type="button"
                onClick={() => setMaximized((m) => !m)}
                className="icon-btn hidden sm:inline-flex"
                aria-label={maximized ? 'Restore size' : 'Maximize'}
              >
                {maximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
            )}
            <button type="button" onClick={onClose} className="icon-btn" aria-label="Close">
              <X size={17} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
